import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, expand, Observable, reduce } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateProjectIntegrationRequest,
  IssuesCounts,
  IssuesPage,
  JiraIssue,
  ProjectIntegration,
  TestConnectionResult,
  UpdateProjectIntegrationRequest,
} from '../models/project-integration.model';

@Injectable({ providedIn: 'root' })
export class ProjectIntegrationService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1/projects`;

  list(projectId: string): Observable<ProjectIntegration[]> {
    return this.http.get<ProjectIntegration[]>(`${this.base}/${projectId}/integrations`);
  }

  create(projectId: string, request: CreateProjectIntegrationRequest): Observable<ProjectIntegration> {
    return this.http.post<ProjectIntegration>(`${this.base}/${projectId}/integrations`, request);
  }

  update(projectId: string, id: string, request: UpdateProjectIntegrationRequest): Observable<ProjectIntegration> {
    return this.http.patch<ProjectIntegration>(`${this.base}/${projectId}/integrations/${id}`, request);
  }

  delete(projectId: string, id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${projectId}/integrations/${id}`);
  }

  testConnection(projectId: string, id: string): Observable<TestConnectionResult> {
    return this.http.post<TestConnectionResult>(`${this.base}/${projectId}/integrations/${id}/test`, null);
  }

  getCounts(projectId: string, integrationId: string): Observable<IssuesCounts> {
    return this.http.get<IssuesCounts>(`${this.base}/${projectId}/integrations/${integrationId}/issues/counts`);
  }

  getIssues(projectId: string, integrationId: string, startAt: number, maxResults: number): Observable<IssuesPage> {
    const params = new HttpParams().set('startAt', startAt.toString()).set('maxResults', maxResults.toString());
    return this.http.get<IssuesPage>(`${this.base}/${projectId}/integrations/${integrationId}/issues`, { params });
  }

  /** On-demand live status of a single linked issue, provider-neutral (NEX-780). */
  getSingleIssue(projectId: string, integrationId: string, issueKey: string): Observable<JiraIssue> {
    return this.http.get<JiraIssue>(
      `${this.base}/${projectId}/integrations/${integrationId}/issues/${encodeURIComponent(issueKey)}`,
    );
  }

  getAllIssues(projectId: string, integrationId: string): Observable<{ issues: JiraIssue[]; truncated: boolean }> {
    const PAGE_SIZE = 100;
    const MAX_PAGES = 50;
    let pagesLoaded = 0;
    let truncated = false;
    return this.getIssues(projectId, integrationId, 0, PAGE_SIZE).pipe(
      expand((page: IssuesPage) => {
        pagesLoaded++;
        if (pagesLoaded >= MAX_PAGES) {
          truncated = true;
          return EMPTY;
        }
        const loaded = page.startAt + page.issues.length;
        if (loaded < page.total && page.issues.length > 0) {
          return this.getIssues(projectId, integrationId, loaded, PAGE_SIZE);
        }
        return EMPTY;
      }),
      reduce(
        (acc: { issues: JiraIssue[]; truncated: boolean }, page: IssuesPage) => ({
          issues: [...acc.issues, ...page.issues],
          truncated,
        }),
        { issues: [], truncated: false },
      ),
    );
  }
}
